<template>
  <net-popup-menu>
    <template #title>{{ $t("objectNames.resource") }}</template>

    <input-text-editor
      :label="$t('labels.description')"
      :value="ctx.resource.description"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div class="line gap-4">
      <input-number
        :label="$t('labels.minValue')"
        :value="ctx.resource.minValue"
        :min="null"
        step="any"
        @update:value="ctx.updateKey('minValue', $event)"
      />
      <input-number
        :label="$t('labels.maxValue')"
        :value="ctx.resource.maxValue"
        :min="null"
        step="any"
        @update:value="ctx.updateKey('maxValue', $event)"
      />
    </div>

    <div v-if="features.momentum">
      <button v-if="momentum == null" type="button" class="button" @click="createMomentum">
        {{ $t("actions.createMomentum") }}
      </button>
      <button-item
        v-if="momentum"
        class="mt-2"
        :icons="['trash-2']"
        @click:trash-2="deleteMomentum()"
        @click="emit('select', momentum)"
      >
        {{ $t("labels.editX", [$t("objectNames.momentum")]) }}
      </button-item>
    </div>

    <div v-if="features.stakeholder">
      <h3 class="label mt-0">{{ $t("objectNames.stakeholder", 4) }}</h3>
      <stakeholder-list
        item-type="resource"
        :item-id="ctx.resource.id"
        :select-enabled="false"
        @select="emit('select', $event)"
      />
    </div>

    <div class="column gap-4" v-if="features.design">
      <input-switch
        :value="ctx.treeNode?.visible"
        @update:value="ctx.updateNodeKey('visible', $event)"
      >
        {{ $t("labels.visible") }}
      </input-switch>

      <template v-if="ctx.treeNode && ctx.treeNode.displayPosition === 'scene' && ctx.sceneNode">
        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.valueStyle") }}</legend>
          <div class="column gap-2">
            <input-switch
              :value="ctx.sceneNode.data.valueVisible"
              @update:value="ctx.updateNodeDataKey('valueVisible', $event)"
            >
              {{ $t("labels.elementValueVisible") }}
            </input-switch>
            <input-switch
              :value="ctx.sceneNode.data.barVisible"
              @update:value="ctx.updateNodeDataKey('barVisible', $event)"
            >
              {{ $t("labels.elementBarVisible") }}
            </input-switch>
          </div>
          <input-switch
            v-if="ctx.sceneNode.data.barVisible"
            :value="ctx.sceneNode.data.barWidthLimited"
            @update:value="ctx.updateNodeDataKey('barWidthLimited', $event)"
          >
            {{ $t("labels.barWidthLimited") }}
          </input-switch>
        </fieldset>

        <!-- hide title -->
        <input-switch
          :value="ctx.sceneNode.data.headlineHidden"
          :label="$t('labels.hideElementTitle')"
          :text-on="$t('labels.yes')"
          :text-off="$t('labels.no')"
          @update:value="ctx.updateNodeDataKey('headlineHidden', $event)"
        />
      </template>
    </div>

    <button
      class="button button_secondary mv-2"
      type="button"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteResource", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed } from "vue";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import ButtonItem from "@core/input/ButtonItem.vue";
import InputTextEditor from '@core/input/TextEditor.vue';
import InputNumber from "@core/input/InputNumber.vue";
import InputSwitch from '@core/input/InputSwitch.vue';
import StakeholderList from "@editor/items/stakeholder/StakeholderList.vue";

import { useResource } from "@editor/items/resource/useResource";
import { useFeatures } from "@editor/store/features.store";
import { useMomentumStore } from "../momentum/momentums.store";

import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  resourceId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"momentum" | "stakeholder"> }): void;
}>();

const features = useFeatures();

const ctx = useResource(() => props.resourceId);

// =====================
// MOMENTUM

const momentum = computed(() => {
  return useMomentumStore().byElementUid[ctx.resource.uid];
});

async function createMomentum() {
  if (momentum.value != null) {
    return;
  }
  await useMomentumStore().create({
    elementType: "resource",
    elementId: props.resourceId
  });
}

function deleteMomentum() {
  if (momentum.value != null) {
    useMomentumStore().delete(momentum.value);
  }
}
</script>